import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import CountUp from 'react-countup';
import { Skeleton } from '../../Components/Common/Skeleton';
import { usePermission } from '../../contexts/PermissionContext';
import {
  decorateAlerts,
  formatDateTime,
  greeting,
  timeAgo,
} from './dashboardUtils';

const TREND_ICON = {
  up: 'ri-arrow-up-line',
  down: 'ri-arrow-down-line',
  flat: 'ri-subtract-line',
  new: 'ri-add-line',
};

/** Small "▲ 12% vs last month" chip. Neutral colours: more or fewer procurements is not good or bad by itself. */
export const TrendChip = ({ trend, suffix = 'vs last month' }) => {
  if (!trend || trend.direction === 'none') {
    return <span className='text-muted fs-12'>No change data yet</span>;
  }
  const text =
    trend.direction === 'new'
      ? `${trend.delta} new, none last month`
      : trend.direction === 'flat'
        ? `Same ${suffix}`
        : `${trend.percent}% ${suffix}`;
  return (
    <span className='d-inline-flex align-items-center gap-1 fs-12 text-muted'>
      <span className='badge bg-light text-body d-inline-flex align-items-center px-1'>
        <i className={TREND_ICON[trend.direction]} aria-hidden='true' />
      </span>
      {text}
    </span>
  );
};

TrendChip.propTypes = {
  trend: PropTypes.object,
  suffix: PropTypes.string,
};

/** Headline number. When `to` is given the whole card is a link to the module behind it. */
export const KpiCard = ({
  label,
  value,
  icon,
  color = 'primary',
  hint,
  trend,
  to,
  emphasis = false,
}) => {
  const body = (
    <CardBody className='p-3'>
      <div className='d-flex align-items-start justify-content-between gap-2'>
        <div className='min-width-0'>
          <p className='text-muted text-uppercase fw-medium fs-12 mb-2 text-truncate'>
            {label}
          </p>
          <h2 className={`fw-semibold mb-1 ${emphasis ? `text-${color}` : ''}`}>
            {value == null ? (
              <span className='text-muted'>-</span>
            ) : (
              <CountUp start={0} end={Number(value) || 0} duration={1} />
            )}
          </h2>
        </div>
        <span
          className={`avatar-sm flex-shrink-0 rounded-3 bg-${color}-subtle text-${color} d-inline-flex align-items-center justify-content-center`}
        >
          <i className={`${icon} fs-22`} aria-hidden='true' />
        </span>
      </div>
      <div className='mt-1 text-muted fs-12'>
        {value == null ? (
          'Not available yet'
        ) : trend ? (
          <TrendChip trend={trend} />
        ) : (
          hint
        )}
      </div>
    </CardBody>
  );

  return (
    <Card
      className={`h-100 mb-0 dashboard-kpi ${to ? 'dashboard-kpi--link' : ''}`}
    >
      {to ? (
        <Link to={to} className='text-reset text-decoration-none d-block h-100'>
          {body}
        </Link>
      ) : (
        body
      )}
    </Card>
  );
};

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  hint: PropTypes.node,
  trend: PropTypes.object,
  to: PropTypes.string,
  emphasis: PropTypes.bool,
};

export const KpiSkeleton = ({ count = 4 }) => (
  <Row className='g-3' role='status' aria-busy='true'>
    <span className='visually-hidden'>Loading</span>
    {Array.from({ length: count }).map((_, i) => (
      <Col key={i} xl={3} sm={6}>
        <Card className='mb-0'>
          <CardBody className='p-3'>
            <Skeleton width='60%' height={12} className='mb-3' />
            <Skeleton width='40%' height={30} className='mb-2' />
            <Skeleton width='70%' height={12} />
          </CardBody>
        </Card>
      </Col>
    ))}
  </Row>
);

KpiSkeleton.propTypes = { count: PropTypes.number };

const SEVERITY = {
  danger: { icon: 'ri-error-warning-line', tone: 'danger' },
  warning: { icon: 'ri-time-line', tone: 'warning' },
  info: { icon: 'ri-information-line', tone: 'info' },
};

/** What needs doing now, most urgent first. This is the most important block after the headline KPIs. */
export const AlertPanel = ({ alerts }) => {
  const { can, isMenuLoading } = usePermission();
  const items = decorateAlerts(alerts);

  return (
    <Card className='mb-0'>
      <CardHeader className='bg-transparent d-flex align-items-center gap-2'>
        <h5 className='card-title mb-0 flex-grow-1'>Needs attention</h5>
        {items.length > 0 && (
          <Badge color={items[0].severity} pill>
            {items.length}
          </Badge>
        )}
      </CardHeader>
      <CardBody className='p-0'>
        {items.length === 0 ? (
          <div className='text-center text-muted py-4 px-3' role='status'>
            <div className='mb-1'>
              <i
                className='ri-checkbox-circle-line fs-24 text-success'
                aria-hidden='true'
              />
            </div>
            <p className='mb-0 fs-14'>
              All clear. Nothing needs your attention right now.
            </p>
          </div>
        ) : (
          <ul className='list-unstyled mb-0'>
            {items.map(item => {
              const meta = SEVERITY[item.severity] || SEVERITY.info;
              const allowed = !isMenuLoading && can('canRead', item.link);
              return (
                <li
                  key={item.key}
                  className='d-flex flex-wrap flex-sm-nowrap align-items-start gap-2 gap-sm-3 px-3 py-3 border-top dashboard-alert'
                >
                  <span
                    className={`flex-shrink-0 avatar-xs rounded-circle bg-${meta.tone}-subtle text-${meta.tone} d-inline-flex align-items-center justify-content-center`}
                  >
                    <i className={meta.icon} aria-hidden='true' />
                  </span>
                  <div className='flex-grow-1 min-width-0'>
                    <p className='mb-0 fw-medium'>{item.title}</p>
                    <p className='mb-0 text-muted fs-13'>{item.hint}</p>
                  </div>
                  {allowed && (
                    <Link
                      to={item.link}
                      className='btn btn-sm btn-soft-secondary flex-shrink-0'
                    >
                      {item.action}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

AlertPanel.propTypes = { alerts: PropTypes.array };

/** Shortcuts the user is actually allowed to open. Renders nothing when there are none. */
export const QuickActions = ({ actions }) => {
  const { can, isMenuLoading } = usePermission();
  if (isMenuLoading) return null;
  const allowed = actions.filter(a => can('canRead', a.to));
  if (allowed.length === 0) return null;

  return (
    <div
      className='d-flex flex-wrap gap-2'
      role='group'
      aria-label='Quick actions'
    >
      {allowed.map((a, i) => (
        <Link
          key={a.to}
          to={a.to}
          className={`btn btn-sm ${i === 0 ? 'btn-primary' : 'btn-soft-primary'}`}
        >
          <i className={`${a.icon} align-bottom me-1`} aria-hidden='true' />
          {a.label}
        </Link>
      ))}
    </div>
  );
};

QuickActions.propTypes = { actions: PropTypes.array.isRequired };

export const DashboardHeader = ({
  user,
  subtitle,
  updatedAt,
  onRefresh,
  loading,
  children,
}) => {
  const name = user?.name || user?.userName || user?.username || '';
  return (
    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
      <div>
        <h4 className='mb-1 fw-semibold'>
          {greeting()}
          {name ? `, ${name}` : ''}
        </h4>
        <p className='text-muted mb-0 fs-14'>{subtitle}</p>
      </div>
      <div className='d-flex flex-wrap align-items-center gap-3'>
        {children}
        <div className='d-flex align-items-center gap-2 text-muted fs-12'>
          {updatedAt && <span>Updated {formatDateTime(updatedAt)}</span>}
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={onRefresh}
            disabled={loading}
            aria-label='Refresh dashboard'
          >
            <i
              className={`ri-refresh-line align-bottom ${loading ? 'dashboard-spin' : ''}`}
              aria-hidden='true'
            />
          </button>
        </div>
      </div>
    </div>
  );
};

DashboardHeader.propTypes = {
  user: PropTypes.object,
  subtitle: PropTypes.string,
  updatedAt: PropTypes.instanceOf(Date),
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  children: PropTypes.node,
};

export const ActivityFeed = ({ items }) => {
  const { can, isMenuLoading } = usePermission();
  const canViewAll =
    !isMenuLoading && can('canRead', '/apps-activity-log/view');

  return (
    <Card className='h-100 mb-0'>
      <CardHeader className='bg-transparent d-flex align-items-center'>
        <h5 className='card-title mb-0 flex-grow-1'>Your recent activity</h5>
        {canViewAll && (
          <Link to='/apps-activity-log/view' className='fs-13'>
            View all
          </Link>
        )}
      </CardHeader>
      <CardBody>
        {items.length === 0 ? (
          <p className='text-muted fs-14 mb-0' role='status'>
            Nothing yet. Your actions will be listed here.
          </p>
        ) : (
          <ul className='list-unstyled mb-0 dashboard-feed'>
            {items.map((a, i) => (
              <li key={`${a.createdDate}-${i}`} className='dashboard-feed-item'>
                <p className='mb-0 fs-14'>{a.description || a.action}</p>
                <p className='mb-0 text-muted fs-12'>
                  {[a.module, a.referenceNo].filter(Boolean).join(' · ')}
                  {(a.module || a.referenceNo) && ' · '}
                  <span title={formatDateTime(a.createdDate)}>
                    {timeAgo(a.createdDate)}
                  </span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

ActivityFeed.propTypes = { items: PropTypes.array };
ActivityFeed.defaultProps = { items: [] };

/** Shown when the API only has the older dashboard endpoints (see useDashboardData). */
export const LimitedNotice = ({ sections }) => (
  <div
    className='alert alert-info d-flex align-items-start gap-2 mb-3'
    role='status'
  >
    <i className='ri-information-line fs-16' aria-hidden='true' />
    <div className='fs-13'>
      <strong>Limited view.</strong> The server does not have the latest
      dashboard update yet, so {sections} will appear after it is deployed.
    </div>
  </div>
);

LimitedNotice.propTypes = { sections: PropTypes.string.isRequired };

export const DashboardError = ({ message, onRetry }) => (
  <Card>
    <CardBody className='text-center py-5' role='alert'>
      <i
        className='ri-cloud-off-line fs-1 text-muted d-block mb-2'
        aria-hidden='true'
      />
      <h5 className='mb-1'>Failed to load the dashboard</h5>
      <p className='text-muted mb-3'>
        {message} Check your connection and try again.
      </p>
      <button type='button' className='btn btn-primary' onClick={onRetry}>
        <i className='ri-refresh-line align-bottom me-1' aria-hidden='true' />
        Try again
      </button>
    </CardBody>
  </Card>
);

DashboardError.propTypes = {
  message: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
};
